export interface ProjectFeature {
    title: string;
    description: string;
    icon: string;
    color: string;
}

export interface ProjectDetails {
    features?: ProjectFeature[];
    privacyPolicy?: string;
    customContent?: {
        type: 'features' | 'privacy' | 'custom';
        content: React.ReactNode;
    };
}

export const projectDetails: Record<string, ProjectDetails> = {
    "21-days-of-habit": {
        features: [
            {
                title: "Modern UI/UX",
                description: "Clean, intuitive interface with smooth animations and parallax effects.",
                icon: "Eye",
                color: "blue"
            },
            {
                title: "Offline-First",
                description: "All features work without internet. Your data and reminders are always available.",
                icon: "Smartphone",
                color: "green"
            },
            {
                title: "Flexible Tracking",
                description: "Track daily, interval, and multi-completion habits (e.g., twice or thrice a day).",
                icon: "CheckCircle",
                color: "purple"
            },
            {
                title: "Progress Visualization",
                description: "See your progress with beautiful circular and linear progress indicators.",
                icon: "TrendingUp",
                color: "orange"
            },
            {
                title: "Smart Reminders",
                description: "Get daily, interval, and streak reminders—fully offline, no external servers.",
                icon: "Bell",
                color: "red"
            },
            {
                title: "Customizable Themes",
                description: "Choose between light, dark, or system themes, and adjust contrast for accessibility.",
                icon: "Palette",
                color: "indigo"
            },
            {
                title: "Privacy-First",
                description: "All your data stays on your device. No personal information is collected or transmitted.",
                icon: "Shield",
                color: "emerald"
            },
            {
                title: "User-Friendly Ads",
                description: "Minimal, non-disruptive ads. First 5 habits are free, then rewarded ads for more.",
                icon: "Zap",
                color: "pink"
            }
        ],
        privacyPolicy: `# Privacy Policy
_Last updated: 2025-07-28_

**21 Days Of Habit** ("the App") is committed to protecting your privacy. This Privacy Policy explains how your information is handled when you use the App.

## 1. Information We Collect
**We do NOT collect, store, or transmit any personal data to our servers or any third-party servers.**
- **No Account Required:** You do not need to create an account or provide any personal information to use the App.
- **Local Data Only:** All your habit data, preferences, and settings are stored locally on your device. We do not have access to this information.

## 2. Local Notifications
The App uses local notifications to remind you about your habits and milestones. These notifications are generated and managed entirely on your device. No notification data is sent to any external server.

## 3. Advertising (AdMob)
The App displays ads using Google AdMob:
- AdMob may collect device information (such as device identifiers, IP address, and usage data) to serve relevant ads. This data is handled by Google according to their [Privacy Policy](https://policies.google.com/privacy).
- We do not access, store, or transmit any of this information ourselves.
- You may see personalized or non-personalized ads depending on your device settings and region.

## 4. Analytics
The App does **not** use any analytics or tracking services. We do not collect usage statistics or track your behavior.

## 5. Data Security
All your data remains on your device. If you uninstall the App, your data will be deleted and cannot be recovered.

## 6. Children's Privacy
The App is not intended for children under the age of 13. We do not knowingly collect any personal information from children.

## 7. Changes to This Policy
We may update this Privacy Policy from time to time. Any changes will be posted within the App or on this page. Your continued use of the App after changes indicates your acceptance of the updated policy.

## 8. Contact Us
If you have any questions or concerns about this Privacy Policy, please contact us at: gauravgadhari39@gmail.com

---
**Summary:**
- No personal data is collected or stored by us.
- All habit and settings data stays on your device.
- Ads are served by Google AdMob, which may collect device data for ad purposes.
- No analytics or tracking is used.`
    },

    "clipmind": {
        features: [
            {
                title: "AI-Powered Intelligence",
                description: "Advanced AI algorithms automatically categorize and understand your clipboard content.",
                icon: "Brain",
                color: "purple"
            },
            {
                title: "Semantic Understanding",
                description: "Understands context and patterns in your workflow for better organization.",
                icon: "Search",
                color: "blue"
            },
            {
                title: "Instant Processing",
                description: "Powered by Groq API and LLaMA 3 70B for lightning-fast AI processing.",
                icon: "Zap",
                color: "yellow"
            },
            {
                title: "Complete Privacy",
                description: "All processing happens locally with no data transmission to external servers.",
                icon: "Shield",
                color: "green"
            },
            {
                title: "Cross-Platform",
                description: "React Native architecture ensures seamless performance across all devices.",
                icon: "Smartphone",
                color: "indigo"
            },
            {
                title: "Workflow Optimization",
                description: "Automatically detects patterns and provides contextual suggestions.",
                icon: "TrendingUp",
                color: "orange"
            }
        ]
    },

    "daily-rise-wellness": {
        features: [
            {
                title: "Premium Product Curation",
                description: "Carefully selected wellness products with comprehensive information and reviews.",
                icon: "Star",
                color: "yellow"
            },
            {
                title: "Educational Content",
                description: "Holistic wellness ecosystem that educates and empowers customers.",
                icon: "BookOpen",
                color: "blue"
            },
            {
                title: "Lightning-Fast Performance",
                description: "Optimized for speed with responsive design across all devices.",
                icon: "Zap",
                color: "green"
            },
            {
                title: "Intuitive Checkout",
                description: "Seamless checkout process that converts visitors into satisfied customers.",
                icon: "ShoppingCart",
                color: "purple"
            },
            {
                title: "Modern Design Aesthetic",
                description: "Calming, trustworthy environment reflecting authentic wellness commitment.",
                icon: "Palette",
                color: "pink"
            }
        ]
    },

    "dochouse": {
        features: [
            {
                title: "Google Sign-In",
                description: "Secure authentication with your Google account for seamless access.",
                icon: "Shield",
                color: "blue"
            },
            {
                title: "Google Drive Sync",
                description: "Automatically backup and sync your documents to Google Drive with your permission.",
                icon: "Smartphone",
                color: "green"
            },
            {
                title: "Smart Categorization",
                description: "Organize documents with custom categories, tags, and metadata for easy retrieval.",
                icon: "Search",
                color: "purple"
            },
            {
                title: "Biometric Security",
                description: "Protect sensitive documents with fingerprint or face authentication.",
                icon: "Shield",
                color: "red"
            },
            {
                title: "Document Sharing",
                description: "Securely share documents with other DocHouse users with granular permissions.",
                icon: "Eye",
                color: "orange"
            },
            {
                title: "Offline Access",
                description: "Access your documents even without internet connection.",
                icon: "Zap",
                color: "indigo"
            },
            {
                title: "Push Notifications",
                description: "Stay updated with document expiry reminders and important updates.",
                icon: "Bell",
                color: "pink"
            },
            {
                title: "Encrypted Storage",
                description: "All data transmissions are encrypted using industry-standard protocols (HTTPS/TLS).",
                icon: "Shield",
                color: "emerald"
            }
        ],
        privacyPolicy: `# Privacy Policy for DocHouse
_Last Updated: December 6, 2024_

**Gaurav Gadhari** ("we", "our", or "us") operates the DocHouse mobile application (the "App"). This Privacy Policy explains how we collect, use, and protect your information.

## 1. Information We Collect

### a) Account Information
When you sign in with Google, we receive your **name, email address, and profile picture** from your Google account.

### b) Documents & Data You Create
- Documents you scan, create, or upload within the app
- Document metadata (titles, categories, tags, dates)
- Profile information you provide (name, phone, address)

### c) Automatically Collected Information
- Device information (device model, OS version)
- App usage data and crash reports
- Push notification tokens

## 2. How We Use Your Information
We use your information to:
- Provide and maintain the App's functionality
- Sync your documents to Google Drive (with your permission)
- Send push notifications about document updates
- Enable document sharing with other users
- Improve the App and user experience

## 3. Data Storage & Security
- **Cloud Storage:** Documents are stored securely in Google Drive and our database provider (Supabase)
- **Local Storage:** Some data is stored locally on your device for offline access
- **Biometric Security:** The app supports fingerprint/face authentication to protect your documents
- **Encryption:** All data transmissions are encrypted using industry-standard protocols (HTTPS/TLS)

## 4. Third-Party Services
DocHouse uses the following third-party services:

| Service | Purpose | Privacy Policy |
|---------|---------|----------------|
| Google Sign-In | Authentication | [Google Privacy Policy](https://policies.google.com/privacy) |
| Google Drive | Cloud document storage | [Google Privacy Policy](https://policies.google.com/privacy) |
| Supabase | Backend database | [Supabase Privacy Policy](https://supabase.com/privacy) |
| Expo | App framework & notifications | [Expo Privacy Policy](https://expo.dev/privacy) |

## 5. Data Sharing
We do not sell your personal information. We may share data only:
- With your consent (e.g., when you share documents with other users)
- With third-party service providers who assist in operating the App
- If required by law or legal process

## 6. Your Rights
You have the right to:
- Access your personal data
- Delete your account and associated data
- Export your documents
- Opt-out of push notifications

## 7. Children's Privacy
DocHouse is not intended for users under 13 years of age. We do not knowingly collect information from children under 13.

## 8. Changes to This Policy
We may update this Privacy Policy periodically. We will notify you of significant changes through the App or via email.

## 9. Contact Us
If you have questions about this Privacy Policy, please contact us at:

📧 **Email:** gauravgadhari39@gmail.com
🌐 **Website:** https://gaurav-gadhari.vercel.app`
    },

    "quicksplit": {
        features: [
            {
                title: "Instant Bill Splitting",
                description: "Split any bill amount among any number of people with just a few taps.",
                icon: "Zap",
                color: "blue"
            },
            {
                title: "Tip Calculator",
                description: "Add custom tip percentages to your bill before splitting for accurate totals.",
                icon: "TrendingUp",
                color: "green"
            },
            {
                title: "Split History",
                description: "Keep track of all your past splits for easy reference and expense tracking.",
                icon: "BookOpen",
                color: "purple"
            },
            {
                title: "Customizable UI",
                description: "Personalize your experience with adjustable font sizes, currency symbols, and themes.",
                icon: "Palette",
                color: "orange"
            },
            {
                title: "Offline-First",
                description: "All calculations and history are stored locally—no internet required.",
                icon: "Smartphone",
                color: "indigo"
            },
            {
                title: "Privacy-Focused",
                description: "Your data stays on your device. No personal information is collected or transmitted.",
                icon: "Shield",
                color: "emerald"
            }
        ],
        privacyPolicy: `# Privacy Policy for QuickSplit
_Last Updated: December 7, 2025_

## Introduction
QuickSplit ("we", "our", or "the app") is a bill splitting calculator app. This Privacy Policy explains how we handle your information.

## Information We Collect

### Local Data (Stored on Your Device Only)
- **Split History:** Records of your bill calculations including amounts, number of people, and tip percentages
- **App Preferences:** Your UI settings like currency symbol, font size, and theme preferences

This data is stored locally on your device using secure local storage (MMKV) and is **never transmitted to our servers**.

### Advertising Data
We use Google AdMob to display advertisements. Google may collect:
- Device advertising identifier
- Ad interaction data
- General device information

For more information, see [Google's Privacy Policy](https://policies.google.com/privacy).

## How We Use Your Information
- **Local Data:** Used solely to provide app functionality and remember your preferences
- **Ad Data:** Used by Google to serve relevant advertisements

## Third-Party Services

| Service | Purpose | Data Shared |
|---------|---------|-------------|
| Google AdMob | Display advertisements | Device advertising ID, ad interaction data |
| GitHub Raw Content | Fetch app announcements | None (read-only) |

## Data Sharing
We do not sell, trade, or transfer your personal information to third parties. The only third-party service with access to any data is Google AdMob for advertising purposes.

## Data Security
All your bill splitting data remains on your device. We do not have access to, nor do we collect, any personal information from you.

## Children's Privacy
QuickSplit does not knowingly collect personal information from children under 13. The app is designed for general audiences.

## Data Deletion
You can delete all your data by:
1. Clearing the app data in your device settings, or
2. Uninstalling the app

## Changes to This Policy
We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy within the app.

## Contact Us
If you have questions about this Privacy Policy, please contact us at:

📧 **Email:** gauravgadhari39@gmail.com
🌐 **Website:** https://gaurav-gadhari.vercel.app

---
© 2025 QuickSplit. All rights reserved.`
    },

    "tap-the-dot": {
        features: [
            {
                title: "Simple Gameplay",
                description: "Tap the dots as they appear—easy to understand, challenging to master.",
                icon: "Zap",
                color: "blue"
            },
            {
                title: "High Score Tracking",
                description: "Your best scores are saved locally so you can always try to beat your record.",
                icon: "TrendingUp",
                color: "green"
            },
            {
                title: "No Account Required",
                description: "Jump straight into the action with no login or signup needed.",
                icon: "CheckCircle",
                color: "purple"
            },
            {
                title: "Suitable for All Ages",
                description: "Fun for everyone, from kids to adults. Safe and family-friendly.",
                icon: "Star",
                color: "orange"
            },
            {
                title: "Offline Play",
                description: "Play anytime, anywhere—no internet connection required.",
                icon: "Smartphone",
                color: "indigo"
            },
            {
                title: "Privacy-Focused",
                description: "No personal data collected. Your scores stay on your device.",
                icon: "Shield",
                color: "emerald"
            }
        ],
        privacyPolicy: `# Privacy Policy - Tap The Dot
_Last Updated: December 17, 2025_

## 1. Information We Collect
This app does not collect, store, or share any personal information.

## 2. Local Data
- High scores are stored locally on your device only
- No account or login required

## 3. Advertising
This app uses Google AdMob to display ads. AdMob may collect:
- Device advertising ID
- General location (country/region)
- App usage data for ad personalization

You can opt out of personalized ads in your device settings.

## 4. Children's Privacy
This app is suitable for all ages and does not knowingly collect data from children.

## 5. Third-Party Services

| Service | Purpose | Privacy Policy |
|---------|---------|----------------|
| Google AdMob | Display advertisements | [Google Privacy Policy](https://policies.google.com/privacy) |

## 6. Contact
For questions about this privacy policy:

📧 **Email:** gauravgadhari39@gmail.com
🌐 **Website:** https://gaurav-gadhari.vercel.app

## 7. Changes
We may update this policy occasionally. Check this page for updates.

---
© 2025 Tap The Dot. All rights reserved.`
    }
};

// Helper function to get project details
export const getProjectDetails = (projectId: string): ProjectDetails => {
    return projectDetails[projectId] || {};
};

// Helper function to check if project has specific content
export const hasProjectFeatures = (projectId: string): boolean => {
    return !!projectDetails[projectId]?.features;
};

export const hasProjectPrivacyPolicy = (projectId: string): boolean => {
    return !!projectDetails[projectId]?.privacyPolicy;
};